<template>
  <div
    class=" mx-auto flex flex-col items-center font-Almarai relative overflow-x-hidden min-h-screen "
  >
    <LogoSvg class="absolute" />

    <div class="flex justify-center items-center w-full sm:w-6/12 md:w-4/12 lg:w-3/12 h-24 sm:h-32 md:h-36 mb-4">
      <img src="@/assets/logo-1.webp" alt="logo" class="w-20 sm:w-24 md:w-28" />
    </div>
    <!-- Main Content -->
    <div
      class="flex flex-col justify-center items-center gap-y-4 md:gap-y-6 bg-transparent rounded-md w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-auto  p-4 sm:p-6 md:p-8"
    >
      <!-- Title -->
      <div class="text-center mb-4 sm:mb-6">
        <h2 class="text-lg sm:text-xl md:text-2xl font-bold text-gray-100 mb-2">تأكيد رقم الهاتف</h2>
        <p class="text-sm sm:text-base text-gray-300 px-2">
          تم إرسال رمز التحقق المكون من 5 أرقام إلى رقم هاتفك
        </p>
      </div>

      <!-- OTP Input Section -->
      <div class="w-full max-w-sm mx-auto mb-6 sm:mb-8">
        <div class="flex justify-center items-center gap-2 sm:gap-3 md:gap-4 mb-4">
          <input
            v-for="(digit, index) in otpDigits"
            :key="index"
            :ref="el => otpInputs[index] = el"
            v-model="otpDigits[index]"
            @input="handleOtpInput(index, $event)"
            @keydown="handleKeyDown(index, $event)"
            @paste="handlePaste($event)"
            @focus="handleFocus(index, $event)"
            @touchstart="handleTouchStart(index, $event)"
            type="tel"
            maxlength="1"
            autocomplete="one-time-code"
            class="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 text-center text-lg sm:text-xl md:text-2xl font-bold bg-gray-800 border-2 border-gray-600 rounded-lg text-gray-100 focus:border-[#CB34C5] focus:outline-none focus:ring-2 focus:ring-[#CB34C5] focus:ring-opacity-50 transition-all duration-200"
            inputmode="numeric"
            pattern="[0-9]*"
          />
        </div>
        
        <!-- Error Message -->
        <div v-if="otpError" class="text-red-400 text-sm text-center mb-4">
          {{ otpError }}
        </div>

        <!-- Resend Code -->
        <div class="text-center hidden">
          <p class="text-gray-400 text-sm mb-2">لم تستلم الرمز؟</p>
          <button
            @click="resendOtp"
            :disabled="resendTimer > 0"
            class="text-[#CB34C5] hover:text-[#9d2890] text-sm font-medium transition-colors duration-200 disabled:text-gray-500 disabled:cursor-not-allowed"
          >
            <span v-if="resendTimer > 0">إعادة الإرسال خلال {{ resendTimer }}s</span>
            <span v-else>إعادة إرسال الرمز</span>
          </button>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex flex-col items-center gap-y-3 sm:gap-y-4 justify-center w-full mx-auto">
        <button
          @click.prevent="verifyOtp"
          :disabled="!isOtpComplete || isVerifying"
          class="inline-flex justify-center items-center gap-2 w-full bg-[#CB34C5] hover:bg-[#9d2890] text-gray-100 rounded-md py-3 sm:py-4 text-sm sm:text-base font-semibold transition-colors duration-200 disabled:bg-gray-600 disabled:cursor-not-allowed"
        >
          <span v-if="isVerifying">جاري التحقق...</span>
          <span v-else>تأكيد الرمز</span>
        </button>
        <button
          @click.prevent="$router.go(-1)"
          class="inline-flex justify-center items-center gap-2 w-full border border-gray-400 hover:border-gray-300 text-gray-100 hover:bg-gray-800 rounded-md py-3 sm:py-4 text-sm sm:text-base font-semibold transition-colors duration-200"
        >
          <span> رجوع </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import animationData from "@/assets/loader.json";
import LogoSvg from "@/components/svg/LogoSvg.vue";
import { mapActions } from "pinia";
import { useAuthStore } from '@/stores/auth';

export default {
  components: {
    LogoSvg,
  },
  data() {
    return {
      toast: useToast(),
      loaded: false,
      walletNumber: "",
      otpDigits: ['', '', '', '', ''],
      otpInputs: [],
      otpError: '',
      isVerifying: false,
      resendTimer: 0,
      resendInterval: null,
      lottieOptions: {
        animationData,
        loop: true,
        autoplay: true,
      },
      jsonFile: animationData,
      bravo: {
        bravoAccountId: null,
        amount: 100,
      },
    };
  },

  computed: {
    isOtpComplete() {
      return this.otpDigits.every(digit => digit !== '');
    },
    otpValue() {
      return this.otpDigits.join('');
    }
  },

  mounted() {
    // Auto focus on first input with better timing for mobile
    this.$nextTick(() => {
      setTimeout(() => {
        if (this.otpInputs[0]) {
          this.otpInputs[0].focus();
          this.otpInputs[0].click(); // For better mobile support
        }
      }, 100);
    });
  },

  beforeUnmount() {
    if (this.resendInterval) {
      clearInterval(this.resendInterval);
    }
  },

  methods: {

    ...mapActions(useAuthStore, ["bravoConfirmation"]),
    handleOtpInput(index, event) {
      let value = event.target.value;
      
      // Handle multiple characters (for mobile keyboards that might input multiple digits)
      if (value.length > 1) {
        value = value.slice(-1); // Take only the last character
        event.target.value = value;
      }
      
      // Only allow numeric input
      if (!/^\d*$/.test(value)) {
        event.target.value = '';
        this.otpDigits[index] = '';
        return;
      }

      // Clear current input if new digit is entered
      if (value && this.otpDigits[index]) {
        this.otpDigits[index] = '';
      }

      // Update the digit
      this.otpDigits[index] = value;
      this.otpError = '';

      // Move to next input if value is entered and not the last input
      if (value && index < 4) {
        setTimeout(() => {
          if (this.otpInputs[index + 1]) {
            this.otpInputs[index + 1].focus();
            this.otpInputs[index + 1].click();
          }
        }, 10);
      }

      // Auto-verify if all digits are entered
      if (this.isOtpComplete) {
        setTimeout(() => {
          this.verifyOtp();
        }, 100);
      }
    },

    handleFocus(index, event) {
      // Select all text on focus for better mobile experience
      setTimeout(() => {
        event.target.select();
      }, 10);
      
      // Clear error when user starts typing
      this.otpError = '';
      
      // Auto-clear the current digit for better UX
      if (this.otpDigits[index]) {
        this.otpDigits[index] = '';
      }
    },

    handleTouchStart(index, event) {
      // Ensure the input gets focus on touch devices
      if (event.target !== document.activeElement) {
        event.target.focus();
      }
    },

    handleKeyDown(index, event) {
      // Handle backspace
      if (event.key === 'Backspace') {
        // Clear current digit first
        this.otpDigits[index] = '';
        
        // Move to previous input if current was empty
        if (index > 0) {
          setTimeout(() => {
            if (this.otpInputs[index - 1]) {
              this.otpInputs[index - 1].focus();
              this.otpInputs[index - 1].click();
            }
          }, 10);
        }
      }
      
      // Handle arrow keys
      if (event.key === 'ArrowLeft' && index > 0) {
        setTimeout(() => {
          this.otpInputs[index - 1].focus();
          this.otpInputs[index - 1].click();
        }, 10);
      }
      if (event.key === 'ArrowRight' && index < 4) {
        setTimeout(() => {
          this.otpInputs[index + 1].focus();
          this.otpInputs[index + 1].click();
        }, 10);
      }
    },

    handlePaste(event) {
      event.preventDefault();
      const pastedData = event.clipboardData.getData('text');
      const digits = pastedData.replace(/\D/g, '').slice(0, 5);
      
      // Clear all digits first
      for (let i = 0; i < 5; i++) {
        this.otpDigits[i] = '';
      }
      
      // Fill with pasted digits
      for (let i = 0; i < 5; i++) {
        this.otpDigits[i] = digits[i] || '';
      }

      // Focus on the last filled input or first empty input
      const nextIndex = Math.min(digits.length, 4);
      setTimeout(() => {
        if (this.otpInputs[nextIndex]) {
          this.otpInputs[nextIndex].focus();
          this.otpInputs[nextIndex].click();
        }
      }, 50);

      // Auto-verify if all digits are pasted
      if (this.isOtpComplete) {
        setTimeout(() => {
          this.verifyOtp();
        }, 200);
      }
    },

    async verifyOtp() {
      if (!this.isOtpComplete) {
        this.otpError = 'يرجى إدخال جميع الأرقام';
        return;
      }

      this.isVerifying = true;
      this.otpError = '';

      try {
        await this.verifyPayment();
        
        
        // Navigate to next page or complete the flow
        this.$router.push('/'); // Replace with your desired route
        
      } catch (error) {
        this.otpError = error.message || 'رمز التحقق غير صحيح';
        this.toast.error(this.otpError);
        
        // Clear OTP and focus first input
        this.clearOtp();
      } finally {
        this.isVerifying = false;
      }
    },

    async verifyPayment() {
     try{
        this.bravoConfirmation(this.otpValue).then(response => {
          console.log("Bravo Confirmation Response:", response.data);
          if (response.data.success === true) {

            this.toast.success("تم تأكيد الدفع بنجاح");
          } else {
            this.toast.error("فشل تأكيد الدفع");
          }
        }); 
     }catch(error){
       this.toast.error(error.message || "حدث خطأ أثناء تأكيد الدفع");
     }
    },

    clearOtp() {
      this.otpDigits = ['', '', '', '', ''];
      this.otpError = '';
      setTimeout(() => {
        if (this.otpInputs[0]) {
          this.otpInputs[0].focus();
          this.otpInputs[0].click();
        }
      }, 100);
    },

    resendOtp() {
      if (this.resendTimer > 0) return;

      // Simulate resend API call
      this.toast.info('تم إرسال رمز جديد');
      
      // Start countdown timer
      this.resendTimer = 60;
      this.resendInterval = setInterval(() => {
        this.resendTimer--;
        if (this.resendTimer <= 0) {
          clearInterval(this.resendInterval);
        }
      }, 1000);

      // Clear current OTP
      this.clearOtp();
    }
  },
};
</script>

<style scoped>
/* Your existing styles */
</style>

<style scoped></style>
